import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter as createTanStackRouter } from '@tanstack/react-router';
import { createTRPCClient, httpBatchLink } from '@trpc/client';
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query';
import { ClerkProvider } from '@clerk/clerk-react';
import { PostHogProvider } from '@posthog/react';
import { routeTree } from './routeTree.gen';
import { initPostHog } from './lib/posthog';

// Import AppRouter type from backend
import type { AppRouter } from '@envase/api/client';

// Create QueryClient
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      refetchOnWindowFocus: false,
    },
  },
});

// Determine the API URL - use relative path when accessed through ngrok or when VITE_API_URL is not set
const getApiUrl = () => {
  // PRIORITY 1: Check if we're running on ngrok domain (browser-side check)
  // This has highest priority because when accessed through ngrok, we MUST use the proxy
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname.includes('ngrok.io') || hostname.includes('ngrok.dev')) {
      // Use relative path through Vite proxy when on ngrok
      // This ensures requests go: browser → ngrok → Vite proxy → localhost:3001
      return '/api/trpc';
    }
  }
  
  const viteApiUrl = import.meta.env.VITE_API_URL;
  
  // PRIORITY 2: If VITE_API_URL is explicitly set to empty or relative path, use it
  if (viteApiUrl === '' || viteApiUrl === '/api') {
    return '/api/trpc';
  }
  
  // PRIORITY 3: If VITE_API_URL is set to a full URL, use it (for production/staging)
  if (viteApiUrl && viteApiUrl.startsWith('http')) {
    return `${viteApiUrl}/trpc`;
  }
  
  // PRIORITY 4: Default to localhost for local development
  return 'http://localhost:3001/trpc';
};

// Create the tRPC client (will be used by both proxy and for mutations)
export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: getApiUrl(),
      async headers() {
        // Get token from Clerk if available
        try {
          // @ts-expect-error - Clerk is added to window at runtime
          const clerk = window.Clerk;
          if (clerk && clerk.session) {
            try {
              // Wait for token with a timeout
              const token = await Promise.race([
                clerk.session.getToken(),
                new Promise<string | null>((_, reject) => 
                  setTimeout(() => reject(new Error('Token fetch timeout')), 5000)
                )
              ]) as string | null;
              
              if (token) {
                return {
                  authorization: `Bearer ${token}`,
                };
              }
            } catch (error) {
              console.warn('[TRPC] Error getting Clerk token:', error);
              // Continue without token - some endpoints might be public
            }
          }
        } catch (error) {
          console.warn('[TRPC] Clerk not available:', error);
        }
        return { authorization: '' };
      },
    }),
  ],
});

// Create TRPC proxy with Clerk integration (for queryOptions)
export const trpc = createTRPCOptionsProxy<AppRouter>({
  client: trpcClient,
  queryClient,
});

export function createRouter() {
  const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

  if (!PUBLISHABLE_KEY) {
    throw new Error('Missing Clerk Publishable Key');
  }

  // Initialize PostHog if key is available
  const posthogClient = initPostHog();

  return createTanStackRouter({
    routeTree,
    scrollRestoration: true,
    defaultPreload: 'intent',
    context: {
      trpc,
      queryClient,
    },
    Wrap: ({ children }) => (
      <ClerkProvider publishableKey={PUBLISHABLE_KEY}>
        <PostHogProvider client={posthogClient}>
          <QueryClientProvider client={queryClient}>
            {children}
          </QueryClientProvider>
        </PostHogProvider>
      </ClerkProvider>
    ),
  });
}

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createRouter>;
  }
}

